<template>
    <Transition :name="feedbackTransitionName" mode="out-in">
        <VJoyWrapper
            v-if="isFeedbackVisible"
            :key="`${feedbackState?.icon.name}-${feedbackState?.message}`"
            no-margin
            column-gap="2"
            wrap="nowrap"
            align="flex-start"
            class="prompt-feedback"
            data-testid="prompt-feedback"
        >
            <VJoyIcon
                v-if="feedbackState?.icon.name"
                data-testid="advanced-search-prompt-icon"
                :name="feedbackState?.icon.name"
                :color="feedbackState?.icon.color"
            />
            <p v-html="feedbackState?.message"></p>
        </VJoyWrapper>
    </Transition>
</template>

<script setup lang="ts">
    import {computed, ref, watch} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyIcon, VJoyWrapper, type TJoyIconsNames, type TJoyIconsColors} from '@maltjoy/core-vue';
    import {countWords} from '../../utils/text';

    type Icon = {
        name: TJoyIconsNames;
        color: TJoyIconsColors;
    };

    type FeedbackState = {
        id: number;
        message: string;
        icon: Icon;
    };

    const props = defineProps<{
        prompt?: string;
    }>();

    // Composables
    const {t} = useTranslation();

    // Computed values
    const isFeedbackVisible = computed<boolean>(() => !!props.prompt && props.prompt?.length > 0);

    // Const
    const MIN_WORDS_TO_BE_VALID: number = 10;

    // Refs
    const feedbackTransitionName = ref('slide-down');

    const feedbackState = computed<FeedbackState>(() => {
        if (countWords(props.prompt) < MIN_WORDS_TO_BE_VALID) {
            return {
                id: 1,
                message: t('search.advanced.ai.entrypoint.mode.ai.matching.input.messages.between-1-to-9-words'),
                icon: {name: 'warning', color: 'neutral'},
            };
        } else {
            return {
                id: 2,
                message: t('search.advanced.ai.entrypoint.mode.ai.matching.input.messages.valid'),
                icon: {name: 'heart-fill', color: 'primary'},
            };
        }
    });

    // Handlers
    function getTransitionDirection(fromState: number, toState: number) {
        return fromState > toState ? 'slide-up' : 'slide-down';
    }

    // Watchers
    watch(
        feedbackState,
        (newFeedback, oldFeedback) => {
            if (!newFeedback || !oldFeedback || newFeedback?.id === oldFeedback?.id) {
                return;
            }
            feedbackTransitionName.value = getTransitionDirection(oldFeedback.id, newFeedback.id);
        },
        {immediate: true},
    );
</script>

<style scoped lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .prompt-feedback {
        margin-bottom: var(--joy-core-spacing-6);

        @include mq.screen_md(min) {
            margin-bottom: inherit;
        }

        p {
            font-size: var(--joy-font-size-primary-300);
            color: var(--joy-color-text-default);
            letter-spacing: var(--joy-font-letter-spacing-tight);
        }
    }
</style>
